import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/AddRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import Tooltip from '@components/Tooltip';
import Button from '@components/Button';

const ACTION_COLUMN = '32px';

const buildActionTracks = actionCount => Array(actionCount).fill(ACTION_COLUMN).join(' ');

const Wrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'actionCount',
})(({ actionCount }) => ({
    width: '100%',
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: actionCount > 0 ? `minmax(0, 1fr) ${buildActionTracks(actionCount)}` : '100%',
    gap: '10px',
    alignItems: 'center',
    '& > :first-child': {
        marginBottom: '0 !important',
    },
    '& .MuiFormControl-root': {
        paddingBottom: '0 !important',
    },
}));

const InlineRowWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'actionCount',
})(({ actionCount }) => ({
    gridColumn: '1 / -1',
    display: 'grid',
    gridTemplateColumns: `minmax(0, 1fr) minmax(0, 1fr) ${buildActionTracks(actionCount)}`.trim(),
    gap: 16,
    alignItems: 'center',
    '@media (max-width: 1024px)': {
        gridTemplateColumns: `minmax(0, 1fr) ${buildActionTracks(actionCount)}`.trim(),
        '& > :first-child': {
            gridColumn: '1 / -1',
        },
    },
    '& > div': {
        paddingBottom: '0 !important',
    },
    '& .MuiFormControl-root': {
        paddingBottom: '0 !important',
    },
}));

const ActionButton = styled(IconButton)({
    height: 32,
    padding: 2,
    '& svg': {
        width: 18,
        height: 18,
        '& path': {
            fill: 'var(--text-secondary)',
        },
    },
});

const AddButtonWrapper = styled('span')({
    width: 'max-content',
});

const warned = new Set();

const warnUnlabelled = label => {
    if (import.meta.env.MODE === 'production' || label || warned.has('unlabelled')) return;
    warned.add('unlabelled');
    console.error(
        'FieldWithActions: an icon action needs a `label` for its accessible name. Build repeated-row ' +
            'actions with rowActions() or rowDeleteAction() from @components/FieldWithActions.',
    );
};

const renderActionButtons = actions =>
    actions.map((action, index) => {
        warnUnlabelled(action.label);

        return (
            <Tooltip key={action.label ?? index} title={action.tooltip || ''} placement="top" describeChild>
                <ActionButton onClick={action.onClick} disabled={action.disabled} aria-label={action.label}>
                    {action.icon}
                </ActionButton>
            </Tooltip>
        );
    });

export const AddRowButton = ({ label, onClick, disabled = false }) => (
    <AddButtonWrapper>
        <Button variant="outlined" startIcon={<AddIcon />} disabled={disabled} onClick={onClick}>
            {label}
        </Button>
    </AddButtonWrapper>
);

export const rowDeleteAction = ({ onDelete, disabled = false, t }) => [
    { icon: <DeleteIcon />, label: t('buttons.delete'), onClick: onDelete, disabled },
];

export const rowActions = ({
    index,
    count,
    onAdd,
    onDelete,
    canAdd = true,
    canDelete,
    addLabel,
    disabled = false,
    t,
}) => [
    canAdd &&
        index === count - 1 && {
            icon: <AddIcon />,
            label: addLabel || t('buttons.add'),
            tooltip: addLabel,
            onClick: onAdd,
            disabled,
        },
    (canDelete ?? count > 1) && { icon: <DeleteIcon />, label: t('buttons.delete'), onClick: onDelete, disabled },
];

const FieldWithActions = ({ children, actions = [], className }) => {
    const visibleActions = actions.filter(action => action?.icon);

    return (
        <Wrapper actionCount={visibleActions.length} className={className}>
            {children}
            {renderActionButtons(visibleActions)}
        </Wrapper>
    );
};

export const InlineFieldsRow = ({ children, actions = [], className }) => {
    const visibleActions = actions.filter(action => action?.icon);

    return (
        <InlineRowWrapper actionCount={visibleActions.length} className={className}>
            {children}
            {renderActionButtons(visibleActions)}
        </InlineRowWrapper>
    );
};

export default FieldWithActions;
